import { debounce } from 'es-toolkit';
import React, { useEffect, useMemo, useState } from 'react';

import { TrussEnvelope } from '@paragon/contract-library';
import { LineSegment2D, Polygon2D } from '@paragon/geometry-library';

import { useAppSelector } from '../../../components/hooks';
import { calculateAutomaticRunDimensions } from '../../../library/layout-geometry/run-dimensioning';
import { is3DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import {
  OptimizedDistanceDimension,
  OptimizedDistanceDimensionContainer,
} from '../../dimension/OptimizedDistanceDimension';
import { useInvalidateEveryRender } from '../../hooks/invalidation-hooks';

export function AutomaticRunDimensions(props: {
  elevation: number;
  dimensionedArea: Polygon2D | null;
  prohibitedAreas?: Polygon2D[];
  trussEnvelopes: TrussEnvelope[];
}) {
  const threeDimensional = useAppSelector(is3DLayoutCameraInUse);

  // Creating a large number of DistanceDimensions is expensive, so the use of techniques to avoid unnecessary
  //   re-renders in this component is important. If you make any changes be sure to test with the React Profiler to
  //   ensure that everything is still working as expected.
  const [dimensionSegments, setDimensionSegments] = useState([] as LineSegment2D[]);

  const calculateDimensionSegments = useMemo(
    () =>
      debounce(
        (
          dimensionedArea: Polygon2D | null,
          trussEnvelopesWithInsertionEnvelope: TrussEnvelope[],
          prohibitedAreas?: Polygon2D[],
        ) => {
          setDimensionSegments(
            calculateAutomaticRunDimensions(dimensionedArea, trussEnvelopesWithInsertionEnvelope, prohibitedAreas),
          );
        },
        60,
        { edges: ['leading', 'trailing'] },
      ),
    [setDimensionSegments],
  );

  useEffect(() => {
    calculateDimensionSegments(props.dimensionedArea, props.trussEnvelopes, props.prohibitedAreas);
  }, [calculateDimensionSegments, props.dimensionedArea, props.trussEnvelopes, props.prohibitedAreas]);

  useEffect(() => () => void calculateDimensionSegments.cancel(), [calculateDimensionSegments]);

  useInvalidateEveryRender();

  return (
    <OptimizedDistanceDimensionContainer threeDimensional={threeDimensional}>
      {dimensionSegments.map((dimensionSegment, i) => (
        <OptimizedDistanceDimension
          key={i}
          start={dimensionSegment.basePoint}
          end={dimensionSegment.endPoint}
          elevation={props.elevation}
          distance={dimensionSegment.length}
          withPerpendicularLabel
          symmetricEndpointMarks
        />
      ))}
    </OptimizedDistanceDimensionContainer>
  );
}
